import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import {
  hasAccessToken,
  getAccessToken,
} from '@/lib/token-storage'
import { loginRedirectSearchSchema } from '@/lib/login-redirect-search'
import { redirectIfLoginSignedOutQuery } from '@/lib/handle-signed-out-query'
import {
  getMainAppHandoffHref,
  getSuporteAppBaseUrl,
  getSuporteAppHandoffHref,
  getAdmindeskAppBaseUrl,
  getAdmindeskAppHandoffHref,
} from '@/lib/post-login-redirect'
import { syncImobdeskEntryAppFromSearch } from '@/lib/imobdesk-entry-app'
import { AuthLayout } from '@/components/auth/auth-layout'
import { AbrirContaForm } from '@/components/auth/abrir-conta-form'
import { OtpVerification } from '@/components/auth/otp-verification'
import { completeLoginWithTokens } from '@/lib/complete-login-with-tokens'
import type { LoginSuccessResponse } from '@/api/auth/auth.types'

export const Route = createFileRoute('/cadastro')({
  validateSearch: loginRedirectSearchSchema,
  beforeLoad: async ({ search }) => {
    syncImobdeskEntryAppFromSearch(search)
    await redirectIfLoginSignedOutQuery('/cadastro', search)
    if (!hasAccessToken()) {
      return
    }
    const at = getAccessToken()
    if (!at) {
      return
    }
    if (search.return_app === 'suporte') {
      const sup = getSuporteAppBaseUrl()
      if (!sup) {
        return
      }
      throw redirect({
        href: getSuporteAppHandoffHref(
          { accessToken: at },
          { next: search.next },
        ),
      })
    }
    if (search.return_app === 'admindesk') {
      const ad = getAdmindeskAppBaseUrl()
      if (!ad) {
        return
      }
      throw redirect({
        href: getAdmindeskAppHandoffHref(
          { accessToken: at },
          { next: search.next },
        ),
      })
    }
    const base = import.meta.env.VITE_MAIN_APP_URL as string | undefined
    if (!base?.trim()) {
      return
    }
    throw redirect({
      href: getMainAppHandoffHref(
        {
          accessToken: at,
        },
        { next: search.next },
      ),
    })
  },
  component: CadastroPage,
})

function CadastroPage() {
  const search = Route.useSearch()
  const [showOtpStep, setShowOtpStep] = useState(false)
  const [email, setEmail] = useState('')
  const googleParams = {
    next: search.next,
    return_app: search.return_app,
  }

  const finishLogin = async (tokens: LoginSuccessResponse) => {
    await completeLoginWithTokens(tokens, {
      returnApp: search.return_app,
      next: search.next,
    })
  }

  return (
    <AuthLayout>
      <div className="flex w-full max-w-md flex-col gap-4">
        {showOtpStep ? (
          <OtpVerification
            email={email}
            onBack={() => setShowOtpStep(false)}
            isNewUser
            afterLoginNavigation={finishLogin}
          />
        ) : (
          <>
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold tracking-tight">
                Crie sua conta grátis
              </h1>
              <p className="text-sm text-muted-foreground text-balance">
                Crie sua conta com e-mail ou Google em poucos cliques. É rápido e
                fácil.
              </p>
            </div>

            <AbrirContaForm
              loginSearch={search}
              googleRedirectParams={googleParams}
              onAccountCreated={(createdEmail) => {
                setEmail(createdEmail)
                setShowOtpStep(true)
              }}
            />
          </>
        )}
      </div>
    </AuthLayout>
  )
}
